import { inject, Injectable } from '@angular/core';
import { ISaveConsentModel, Product, UserJournal } from '@mwe/models';
import dayjs from 'dayjs';
import { FeatureToggleEnum, PROFILE_DSGVO_CHECK_ROUTE, PROFILE_ROUTE, TriggeringProcessEnum } from '@mwe/constants';
import { convertUserJournalDateToDayJsObj, isArrayWithMinOneItem } from '@mwe/utils';
import { AccountLogic } from '../../account/account.logic';
import { DsgvoService } from '../dsgvo.service';
import { DsgvoStateService } from '../dsgvo-state.service';
import { UserJournalService } from '../../user/user.journal.service';
import { Router } from '@angular/router';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { LoggingService, ProfileService } from '@mwe/services';

@Injectable({
  providedIn: 'root',
})
export class DsgvoLogic {
  private service = inject(DsgvoService);
  private stateService = inject(DsgvoStateService);
  private userJournalService = inject(UserJournalService);
  private profileService = inject(ProfileService);
  private loggingService = inject(LoggingService);
  private router = inject(Router);
  private accountLogic = inject(AccountLogic);

  DSGVO_SUCCESS = 'dsgvoSuccess';

  async checkForRedirectToDsgvoSettings(products: Product[]): Promise<boolean> {
    if (
      this.accountLogic.isImpersonate() ||
      !isArrayWithMinOneItem(products) ||
      this.profileService.isFeatureToggleDisabled(FeatureToggleEnum.DSGVO_CHECK_ENABLED)
    ) {
      return false;
    }

    try {
      const dsgvoEvents = await this.getAllDSGVOJournalEvents();
      const cancelCondition = !this.checkIfDSGVOShouldBeShown(dsgvoEvents) || this.stateService.hasCheckedIfDsgvoCheckShouldBeOpened;
      if (!this.stateService.shouldCheckDsgvoAtNextPossibleMoment() && cancelCondition) {
        return false;
      }
      this.redirectToDsgvo();
      return true;
    } catch (error) {
      this.loggingService.logError(error, `DsgvoLogic/checkForRedirectToDsgvoSettings`);
      return false;
    }
  }

  async triggerDsgvoForAssociations(gpn: string, systemId: string): Promise<void> {
    const dsgvoData = {
      gruppen: [],
      gpList: [
        {
          geschaeftsPartnerId: gpn,
          sparte: systemId,
        },
      ],
      ausloesenderProzess: TriggeringProcessEnum.INITIAL,
    } as ISaveConsentModel;

    try {
      await this.service.copyConsent(dsgvoData);
    } catch (reason) {
      if (reason.status == 404 && reason.error.code == 1001) {
        this.stateService.activateDsgvoCheckAtNextPossibleMoment();
      }
    }
  }

  checkIfDSGVOShouldBeShown(dsgvoEvents: UserJournal[]): boolean {
    if (!dsgvoEvents || dsgvoEvents?.length === 0) {
      return true;
    }

    const today = dayjs();
    const latestDsgvoJournalEntry = dsgvoEvents?.reduce((a, b) =>
      convertUserJournalDateToDayJsObj(a.modifiedAt).isAfter(convertUserJournalDateToDayJsObj(b.modifiedAt)) ? a : b,
    );
    return this.isCapableToShowDSGVO(latestDsgvoJournalEntry, today);
  }

  isCapableToShowDSGVO(event: UserJournal, today: dayjs.Dayjs): boolean {
    const beginPeriod = convertUserJournalDateToDayJsObj(event.modifiedAt);
    const endPeriod = beginPeriod.add(dayjs.duration(event.additionalData));
    return !today.isBefore(endPeriod);
  }

  async getAllDSGVOJournalEvents(): Promise<UserJournal[]> {
    return await this.userJournalService.getEvents([this.DSGVO_SUCCESS]);
  }

  async updateDsgvoSuccess(): Promise<void> {
    try {
      await this.userJournalService.updateEvent(this.DSGVO_SUCCESS);
    } catch (e) {
      this.loggingService.logError(e, `could not save dsgvo event`);
    }
  }

  private redirectToDsgvo(): void {
    if (this.stateService.wasDsgvoCheckOpened && !this.stateService.shouldCheckDsgvoAtNextPossibleMoment()) {
      return;
    }

    this.stateService.resetDsgvoCheckAtNextPossibleMoment();
    this.stateService.wasDsgvoCheckOpened = true;
    this.stateService.hasCheckedIfDsgvoCheckShouldBeOpened = true;
    this.router.navigateByUrl(`/${PROFILE_ROUTE}/${PROFILE_DSGVO_CHECK_ROUTE}`);
  }
}
